import * as React from "react";
import { useSearchParams } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "framer-motion";
import { useViewportSize } from "@react-aria/utils";
import { zoom, zoomIdentity, type ZoomBehavior } from "d3-zoom";
import { pointer, select } from "d3-selection";
import { interpolateZoom } from "d3-interpolate";
import type { Property } from "csstype";
import { Defs } from "./svg-misc";
import { useMedia } from "./use-media";
import debounce from "just-debounce-it";

export const minZoomScale = 0.95;
const maxZoomScale = 12; //7;
const ratio = 9 / 16;
export const width = 3200;
export const height = ratio * width;

// stupid name
export type XYK = {
  x: number;
  y: number;
  k: number;
};

type ZoomTo = (
  event: React.MouseEvent<SVGElement, MouseEvent>,
  { x, y, k }: XYK
) => void;

type ZoomContextValue = {
  zoomTo: ZoomTo;
  zoomHandler: ZoomBehavior<Element, unknown>;
  resetZoom(e: MouseEvent);
  zoom(event: React.MouseEvent<Element, MouseEvent>, by?: number): void;
};

export function useZoomContext() {
  const contextValue = React.useContext(ZoomContext);

  if (contextValue === undefined) {
    throw new Error("useZoomContext must be used within a ZoomProvider");
  }

  return contextValue;
}

const getInitValue =
  (searchParams: URLSearchParams) => (key: string, defaultValue: number) => {
    const value = searchParams.get(key);

    const numberValue = Number(value);
    if (value && !Number.isNaN(numberValue)) {
      return numberValue;
    }

    return defaultValue;
  };

export const ZoomContext = React.createContext<ZoomContextValue>(null!);
ZoomContext.displayName = "ZoomContext";

export function ZoomContainer({ children }: { children?: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitSearchParams = getInitValue(searchParams);
  const initX = getInitSearchParams("x", 0);
  const initY = getInitSearchParams("y", 0);
  const initK = getInitSearchParams("k", 1);

  const x = useMotionValue(initX);
  const y = useMotionValue(initY);
  const k = useMotionValue(initK);
  const cursor = useMotionValue<Property.Cursor>("grab");
  const prefersReducedMotion = useMedia("(prefers-reduced-motion: reduce)");
  const viewport = useViewportSize();

  useMotionValueEvent(
    x,
    "change",
    debounce((latest) => {
      setSearchParams((params) => {
        params.set("x", String(latest.toFixed(2)));
        return new URLSearchParams(params);
      });
    }, 200)
  );

  useMotionValueEvent(
    y,
    "change",
    debounce((latest) => {
      setSearchParams((params) => {
        params.set("y", String(latest.toFixed(2)));
        return new URLSearchParams(params);
      });
    }, 200)
  );


  useMotionValueEvent(
    k,
    "change",
    debounce((latest) => {
      setSearchParams((params) => {
        params.set("k", String(latest.toFixed(2)));
        return new URLSearchParams(params);
      });
    }, 200)
  );


  const svgRef = React.useRef<SVGSVGElement>(null!);

  const [zoomHandler] = React.useState(() =>
    zoom()
      .scaleExtent([minZoomScale, maxZoomScale])
      .extent([
        [0, 0],
        [width, height],
      ])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      // @ts-expect-error
      .interpolate(interpolateZoom.rho(1.6))
  );

  React.useEffect(
    function initZoomhandler() {
      let initScale = k.get();

      zoomHandler.on("start", () => {
        initScale = k.get();
      });

      zoomHandler.on("zoom", (event) => {
        const { transform } = event;

        if (initScale < transform.k) {
          cursor.set("zoom-in");
        } else if (initScale > transform.k) {
          cursor.set("zoom-out");
        } else {
          cursor.set("grabbing");
        }

        x.set(transform.x);
        y.set(transform.y);
        k.set(transform.k);
      });

      zoomHandler.on("end", () => cursor.set("grab"));

      // @ts-expect-error
      select(svgRef.current).call(zoomHandler);

      select(svgRef.current).call(
        zoomHandler.transform,
        zoomIdentity.translate(initX, initY).scale(initK)
      );
    },
    [zoomHandler, k] // fudge to leave out init zoom values
  );

  const zoomContextValue = React.useMemo(() => {
    const transitionDuration = prefersReducedMotion ? 0 : 600;

    function zoomTo(
      event: React.MouseEvent<SVGElement, MouseEvent>,
      { x, y, k = maxZoomScale }: { x: number; y: number; k?: number }
    ) {
      event.stopPropagation();

      const clickTransform = zoomIdentity
        .translate(width / 2, height / 2)
        .scale(k)
        .translate(-x, -y);

      select(svgRef.current)
        .transition()
        .duration(transitionDuration)
        // @ts-expect-error
        .call(zoomHandler.transform, clickTransform, pointer(event));
    }

    function resetZoom(e) {
      zoomTo(e, {
        x: width / 2,
        y: height / 2,
        k: 1,
      });
    }

    function zoom(event: React.MouseEvent<SVGElement, MouseEvent>, by = 0.25) {
      event.stopPropagation();

      select(svgRef.current)
        .transition()
        .duration(transitionDuration)
        // @ts-expect-error
        .call(zoomHandler.scaleBy, 1 + by);
    }

    return {
      zoomTo,
      zoom,
      resetZoom,
      zoomHandler,
    };
  }, [zoomHandler]);

  return (
    <motion.svg
      ref={svgRef}
      width={viewport.width}
      height={viewport.height}
      viewBox={`0 0 ${width} ${height}`}
      className="m-0"
      style={{
        cursor,
      }}
    >
      <Defs />

      <ZoomContext.Provider value={zoomContextValue}>
        <motion.g
          style={{
            x,
            y,
            scale: k,
            originX: "0px",
            originY: "0px",
          }}
        >
          {children}
        </motion.g>

        {/* <Minimap width={width} height={height} x={x} y={y} k={k} /> */}
      </ZoomContext.Provider>
    </motion.svg>
  );
}

function Minimap({ width, height, x, y, k, scale = 1 / 6 }) {
  const id = "zoom-clip";

  const rectRef = React.useRef(null);
  const { zoomHandler } = useZoomContext();

  const inverseX = useTransform(x, (value: number) => {
    const scale = k.get();
    return -value / scale;
  });

  const inverseY = useTransform(y, (value: number) => {
    const scale = k.get();
    return -value / scale;
  });

  const inverseScale = useTransform(k, (value: number) => 1 / value);

  //   useEffect(() => {
  //     zoomHandler.on("zoom", (event) => {
  //       const { transform } = event;

  //       console.log({ transform });
  //       //   x.set(transform.x);
  //       //   y.set(transform.y);
  //       //   k.set(transform.k);
  //     });

  //     select(rectRef.current).call(zoomHandler);
  //   }, [zoomHandler]);

  return (
    <g
      id="minimap"
      transform={`translate(${(1 - scale) * width},${height / 2})`}
    >
      <clipPath id={id}>
        <rect x="0" y="0" width={width} height={height}></rect>
      </clipPath>
      <g clipPath={`url(#${id})`} transform={`scale(${scale})`}>
        <rect
          width={width}
          height={height}
          fill="white"
          stroke="black"
          strokeWidth={2}
        ></rect>
        <use href="#content"></use>
        <motion.rect
          id="frame"
          width={width}
          height={height}
          fill="white"
          ref={rectRef}
          fillOpacity={0.2}
          stroke="black"
          strokeWidth={4}
          //   filter="url(#minimapDropShadow)"
          style={{
            scale: inverseScale,
            x: inverseX,
            y: inverseY,
            cursor: "move",
            originX: 0,
            originY: 0,
          }}
        ></motion.rect>
      </g>
    </g>
  );
}
